import React, { useState, useCallback } from "react";
import { CssBaseline, Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import Header from "../components/Header/AppBar";
import Sidebar from "../components/Sidebar";
import useGlobalStore from "src/store/useGlobalStore";
import { PATHS } from "src/routes";
import { Redirect } from "react-router-dom";
import { JOB_TYPES } from "src/storage/constants";

const drawerWidth = 258;

const Root = styled.div`
  display: flex;
  min-height: 100vh;
  overflow: hidden;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const AppContent = styled.div`
  flex: 1;
  min-height: 100vh;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  overflow: hidden;
`;

const MainContent = styled.div`
  background: ${(props) => props.theme.palette.background.default};
  height: calc(100vh - 64px);
  overflow-y: scroll;

  padding: ${(props) => (props.$isRetailHomepage ? 0 : 60)}px;

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }

  @media (max-width: 800px) {
    padding: ${(props) => (props.$isRetailHomepage ? 0 : 40)}px;
  }

  @media (max-width: 600px) {
    padding: ${(props) => (props.$isRetailHomepage ? 0 : 20)}px;
  }

  @media (max-width: 500px) {
    padding: ${(props) =>
      props.$isRetailHomepage ? "0px" : "40px 12px 100px 12px"};
  }
`;

const GlobalStyle = createGlobalStyle`
  html,
  body,
  #root {
    height: 100%;
  } 

  body {
    background: ${(props) => props.theme.palette.background.default};
    overflow: hidden;
    height: 100%;
  }

  .MuiCardHeader-action .MuiIconButton-root {
    padding: 4px;
    width: 28px;
    height: 28px;
  }
`;

const Dashboard = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const job = useGlobalStore((state) => state?.config?.user?.job);
  const isRetail = useGlobalStore((state) => state.isRetail);
  const isAuthenticated = useGlobalStore((state) => state.isAuthenticated);
  const isRetailHomePage = location.pathname === PATHS.HOME && isRetail;

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen((prev) => !prev);
  }, []);

  if (!isAuthenticated) {
    return <Redirect to={PATHS.SIGNIN} />;
  }

  const props = React.isValidElement(children) ? children.props : {};

  if (!props.allowVendor && !isRetail) {
    return <Redirect to={{ pathname: PATHS.HOME }} />;
  }

  // If you only have Manager Status.. dont let them go to Non Manager Pages
  if (!props.allowManager && job === JOB_TYPES.SUPERVISOR) {
    return <Redirect to={{ pathname: PATHS.HOME }} />;
  }

  // If you are retail, you do not have a following page
  if (isRetail && !props.allowRetail) {
    return <Redirect to={{ pathname: PATHS.HOME }} />;
  }

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Drawer>
        <Box sx={{ display: { lg: "none", md: "block" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
          />
        </Box>
        <Box
          sx={{
            display: {
              xl: "block",
              lg: "block",
              md: "none",
              sm: "none",
              xs: "none",
            },
          }}
        >
          <Sidebar PaperProps={{ style: { width: drawerWidth } }} />
        </Box>
      </Drawer>
      <AppContent>
        <Header onDrawerToggle={handleDrawerToggle} />
        <MainContent $isRetailHomepage={isRetailHomePage}>
          {children}
        </MainContent>
      </AppContent>
    </Root>
  );
};

export default Dashboard;
